import React, { useState, useEffect } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { Button } from '../ui/button';
import { 
  Gem, 
  Pen, 
  Combine, 
  Check, 
  ArrowRight, 
  Sparkles,
  Shield,
  Clock,
  Eye,
  Scissors,
  Target,
  Zap,
  ChevronDown
} from 'lucide-react';
import './Techniques.css';

// Techniques data with translations
const getTechniques = (language) => [
  {
    id: 'saphir',
    name: 'Saphir FUE',
    subtitle: language === 'de' ? 'Präzision mit Edelstein-Technologie' : 'Precision with Gemstone Technology',
    icon: Gem,
    color: 'from-[#7BA7C2] to-[#5A8BA6]',
    shadowColor: 'shadow-[#7BA7C2]/25',
    image: 'https://customer-assets.emergentagent.com/job_5a750e2c-9afa-457e-8878-f20f1025e07d/artifacts/876q7a14_saphir_fue.webp',
    description: language === 'de' 
      ? 'Die Saphir FUE-Technik verwendet Klingen aus echtem Saphir-Kristall. Diese ermöglichen mikroskopisch kleine, V-förmige Einschnitte für eine dichtere Platzierung der Grafts und schnellere Heilung.'
      : 'The Sapphire FUE technique uses blades made of real sapphire crystal. These enable microscopically small, V-shaped incisions for denser graft placement and faster healing.',
    idealFor: language === 'de' ? 'Große Flächen & maximale Dichte' : 'Large areas & maximum density',
    features: [
      { icon: Target, text: language === 'de' ? 'Präzisere Schnitte als Stahl' : 'More precise cuts than steel' },
      { icon: Shield, text: language === 'de' ? 'Minimale Narbenbildung' : 'Minimal scarring' },
      { icon: Zap, text: language === 'de' ? 'Schnelle Heilung' : 'Fast healing' },
      { icon: Eye, text: language === 'de' ? 'Hohe Überlebensrate' : 'High survival rate' },
    ],
    stats: [
      { value: '0.8mm', label: language === 'de' ? 'Mikro-Inzisionen' : 'Micro-incisions' },
      { value: '95%+*', label: language === 'de' ? 'Anwachsrate' : 'Growth rate' },
      { value: '10', label: language === 'de' ? 'Tage Heilung' : 'Days healing' },
    ],
    benefits: language === 'de' 
      ? ['Ideal für Norwood 4-6 Stadien', 'Bis zu 4.500 Grafts möglich', 'Schnelle Heilung', 'Dichtere Graft-Platzierung']
      : ['Ideal for Norwood 4-6 stages', 'Up to 4,500 grafts possible', 'Fast healing', 'Denser graft placement']
  },
  {
    id: 'dhi',
    name: language === 'de' ? 'DHI Methode' : 'DHI Method',
    subtitle: language === 'de' ? 'Direkte Implantation ohne Rasur' : 'Direct implantation without shaving',
    icon: Pen,
    color: 'from-[#5A8BA6] to-[#7BA7C2]',
    shadowColor: 'shadow-[#5A8BA6]/25',
    image: 'https://customer-assets.emergentagent.com/job_5a750e2c-9afa-457e-8878-f20f1025e07d/artifacts/bp7s9vzb_dhi_fue.webp',
    description: language === 'de'
      ? 'Bei der DHI-Methode (Direct Hair Implantation) werden die Haarfollikel mit einem speziellen Choi-Implanter direkt implantiert - ohne vorherige Kanalöffnung und ohne Rasur des Oberkopfs. Hinweis: Das Spenderhaar muss rasiert werden.'
      : 'With the DHI method (Direct Hair Implantation), hair follicles are implanted directly using a special Choi implanter - without prior channel opening and without shaving the top of the head. Note: Donor hair must be shaved.',
    idealFor: language === 'de' ? 'Ohne Rasur & maximale Diskretion' : 'No shaving & maximum discretion',
    features: [
      { icon: Scissors, text: language === 'de' ? 'Ohne Rasur des Oberkopfs' : 'No shaving of the scalp' },
      { icon: Shield, text: language === 'de' ? 'Weniger Schwellung' : 'Less swelling' },
      { icon: Clock, text: language === 'de' ? 'Schneller alltagstauglich' : 'Faster return to daily life' },
      { icon: Zap, text: language === 'de' ? 'Hohe Überlebensrate' : 'High survival rate' },
    ],
    stats: [
      { value: '100%', label: language === 'de' ? 'Ohne Rasur Oberkopf' : 'No scalp shaving' },
      { value: '95%+*', label: language === 'de' ? 'Anwachsrate' : 'Growth rate' },
      { value: '7', label: language === 'de' ? 'Tage Heilung' : 'Days healing' },
    ],
    benefits: language === 'de'
      ? ['Perfekt für Verdichtungen', 'Ideal für lange Haare', 'Diskret & unauffällig', 'Weniger Schwellung']
      : ['Perfect for densification', 'Ideal for long hair', 'Discreet & unobtrusive', 'Less swelling']
  },
  {
    id: 'kombi',
    name: language === 'de' ? 'Kombinations-Technik' : 'Combination Technique',
    subtitle: language === 'de' ? 'Das Beste aus beiden Welten' : 'The best of both worlds',
    icon: Combine,
    color: 'from-[#9BBFD9] to-[#7BA7C2]',
    shadowColor: 'shadow-[#9BBFD9]/25',
    image: '/images/optimized/saphir-dhi.webp',
    description: language === 'de'
      ? 'Unsere Experten kombinieren Saphir FUE und DHI für optimale Ergebnisse: Saphir FUE für die großen kahlen Bereiche und DHI für die Verdichtung bestehender Haarbereiche - individuell auf Sie abgestimmt.'
      : 'Our experts combine Sapphire FUE and DHI for optimal results: Sapphire FUE for large bald areas and DHI for densification of existing hair areas - individually tailored to you.',
    idealFor: language === 'de' ? 'Maximale Ergebnisse & Flexibilität' : 'Maximum results & flexibility',
    features: [
      { icon: Sparkles, text: language === 'de' ? 'Individuell angepasst' : 'Individually customized' },
      { icon: Target, text: language === 'de' ? 'Optimale Ergebnisse' : 'Optimal results' },
      { icon: Eye, text: language === 'de' ? 'Natürlichste Haarlinie' : 'Most natural hairline' },
      { icon: Shield, text: language === 'de' ? 'Beste Anwachsrate' : 'Best growth rate' },
    ],
    stats: [
      { value: '100%', label: language === 'de' ? 'Ohne Rasur Oberkopf' : 'No scalp shaving' },
      { value: 'Hybrid', label: language === 'de' ? 'Kombinierte Technik' : 'Combined technique' },
      { value: '7-10', label: language === 'de' ? 'Tage Heilung' : 'Days healing' },
    ],
    benefits: language === 'de'
      ? ['Haarlinie mit DHI (ohne Rasur)', 'Hauptbereich mit Saphir FUE', 'Maximale Flexibilität', 'Individueller Behandlungsplan']
      : ['Hairline with DHI (no shaving)', 'Main area with Sapphire FUE', 'Maximum flexibility', 'Individual treatment plan']
  }
];

const TechniqueCard = ({ technique, isActive, onClick, isMobile, isDark }) => {
  const IconComponent = technique.icon;
  
  return (
    <button
      onClick={onClick}
      className={`group relative w-full text-left p-5 sm:p-6 rounded-2xl border transition-all duration-500 overflow-hidden ${
        isDark
          ? isActive 
            ? 'bg-white/[0.05] border-[#7BA7C2]/30' 
            : 'bg-white/[0.02] border-white/10 hover:border-white/20 hover:bg-white/[0.03]'
          : isActive
            ? 'bg-[#5A8BA6]/5 border-[#5A8BA6]/30'
            : 'bg-gray-50 border-gray-200 hover:border-[#5A8BA6]/30 hover:bg-gray-100'
      }`}
    >
      {/* Animated Glow Effect - only on active card and only on mobile */}
      {isActive && isMobile && isDark && (
        <>
          <div className="technique-card-glow" />
          <div className="technique-card-glow-blur" />
        </>
      )}
      
      <div className="relative flex items-center gap-4">
        <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br ${technique.color} flex items-center justify-center flex-shrink-0 ${isActive ? 'scale-110' : 'group-hover:scale-105'} transition-transform duration-500 shadow-lg ${isActive ? technique.shadowColor : ''}`}>
          <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-base sm:text-lg font-semibold transition-colors duration-300 ${
            isDark 
              ? isActive ? 'text-[#7BA7C2]' : 'text-white group-hover:text-[#7BA7C2]'
              : isActive ? 'text-[#5A8BA6]' : 'text-gray-900 group-hover:text-[#5A8BA6]'
          }`}>
            {technique.name}
          </h3>
          <p className={`text-xs sm:text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{technique.idealFor}</p>
        </div>
        {isMobile ? (
          <ChevronDown className={`w-5 h-5 transition-all duration-300 ${
            isDark
              ? isActive ? 'text-[#7BA7C2] rotate-180' : 'text-gray-500'
              : isActive ? 'text-[#5A8BA6] rotate-180' : 'text-gray-400'
          }`} />
        ) : (
          <ArrowRight className={`w-5 h-5 transition-all duration-300 ${
            isDark
              ? isActive ? 'text-[#7BA7C2] translate-x-1' : 'text-gray-500 group-hover:text-white'
              : isActive ? 'text-[#5A8BA6] translate-x-1' : 'text-gray-400 group-hover:text-gray-700'
          }`} />
        )}
      </div>
    </button>
  );
};

const TechniqueDetail = ({ technique, isDark }) => {
  const { language } = useLanguage();
  if (!technique) return null;
  
  const ActiveIcon = technique.icon;
  
  return (
    <div className={`relative h-full rounded-2xl sm:rounded-3xl border overflow-hidden ${
      isDark 
        ? 'bg-gradient-to-br from-white/[0.05] to-white/[0.02] border-white/10'
        : 'bg-white border-gray-200 shadow-lg'
    }`}>
      {/* Decorative Background */}
      <div className={`absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br ${technique.color} opacity-10 rounded-full blur-[100px] transition-all duration-700`} />
      
      <div className="relative p-5 sm:p-8 lg:p-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className={`hidden sm:flex w-16 h-16 sm:w-20 sm:h-20 rounded-xl sm:rounded-2xl bg-gradient-to-br ${technique.color} items-center justify-center shadow-xl ${technique.shadowColor}`}>
            <ActiveIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
              <h3 className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{technique.name}</h3>
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                isDark ? 'bg-[#7BA7C2]/20 text-[#7BA7C2]' : 'bg-[#5A8BA6]/10 text-[#5A8BA6]'
              }`}>
                {technique.idealFor}
              </span>
            </div>
            <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{technique.subtitle}</p>
          </div>
        </div>

        {/* Technique Image */}
        {technique.image && (
          <div className={`mb-6 sm:mb-8 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <img 
              src={technique.image} 
              alt={`${technique.name} Haartransplantation Methode - Dion Hair Clinic`}
              loading="lazy"
              className="w-full h-48 sm:h-56 lg:h-64 object-cover object-center"
            />
          </div>
        )}

        {/* Description */}
        <p className={`text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          {technique.description}
        </p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-8">
          {technique.stats.map((stat, index) => {
            const hasAsterisk = stat.value.includes('*');
            const displayValue = hasAsterisk ? stat.value.replace('*', '') : stat.value;
            
            return (
              <div 
                key={index}
                className={`text-center p-3 sm:p-4 rounded-xl h-[90px] sm:h-[100px] flex flex-col justify-center ${
                  isDark 
                    ? 'bg-white/[0.03] border border-white/5' 
                    : 'bg-gray-50 border border-gray-100'
                }`}
              >
                <div className={`text-xl sm:text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {displayValue}
                  {hasAsterisk && <span className={`text-[10px] align-super ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>*</span>}
                </div>
                <div className={`text-[10px] sm:text-xs uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {technique.features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <div 
                key={index}
                className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl ${
                  isDark 
                    ? 'bg-white/[0.02] border border-white/5' 
                    : 'bg-gray-50 border border-gray-100'
                }`}
              >
                <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br ${technique.color} flex items-center justify-center flex-shrink-0`}>
                  <FeatureIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <span className={`text-sm sm:text-base ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feature.text}</span>
              </div>
            );
          })}
        </div>

        {/* Benefits List */}
        <div className={`p-4 sm:p-6 rounded-xl ${
          isDark 
            ? 'bg-[#7BA7C2]/5 border border-[#7BA7C2]/20' 
            : 'bg-[#5A8BA6]/5 border border-[#5A8BA6]/20'
        }`}>
          <h4 className={`font-semibold mb-3 sm:mb-4 text-sm sm:text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Vorteile dieser Technik:' : 'Benefits of this technique:'}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
            {technique.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2">
                <Check className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                <span className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Techniques = ({ onOpenConsultation }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileActiveIndex, setMobileActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const { features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const containerRef = React.useRef(null);
  const mobileContainerRef = React.useRef(null);

  // Get techniques with current language
  const techniques = getTechniques(language);
  
  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Scroll-based active detection for both mobile and desktop
  useEffect(() => {
    const handleScroll = () => {
      const ref = isMobile ? mobileContainerRef : containerRef;
      const selector = isMobile ? '[data-technique-card-mobile]' : '[data-technique-card]';
      const cards = ref.current?.querySelectorAll(selector);
      if (!cards || cards.length === 0) return;
      
      // Find the card closest to the top of viewport (with some offset)
      const targetPosition = window.innerHeight * 0.3; // 30% from top
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const cardTop = rect.top;
        const distance = Math.abs(cardTop - targetPosition);
        
        // Prefer cards that are visible and near the top
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        }
      });
      
      setMobileActiveIndex(closestIndex);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);
  
  // Only show on hair transplant pages (not beard, eyebrow, or DHI-specific)
  if (features?.analysisType === 'beard' || features?.analysisType === 'eyebrow' || features?.showDhiHybridOnly) {
    return null;
  }
  
  const activeTechnique = techniques[activeIndex];

  return (
    <section id="techniken" className={`relative py-16 sm:py-24 overflow-hidden transition-colors duration-300 ${
      isDark ? 'bg-[#0a0a0a]' : 'bg-white'
    }`}>
      {/* Background */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 right-0 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] rounded-full blur-[80px] ${
          isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-1/4 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full blur-[60px] ${
          isDark ? 'bg-[#5A8BA6]/10' : 'bg-[#7BA7C2]/5'
        }`} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4 sm:mb-6 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
          }`}>
            <Gem className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span className={`text-xs sm:text-sm font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
              {language === 'de' ? 'Modernste Technologien' : 'Latest Technologies'}
            </span>
          </div>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {language === 'de' ? 'Unsere ' : 'Our '}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {language === 'de' ? 'Techniken' : 'Techniques'}
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-base sm:text-lg px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Wir nutzen die fortschrittlichsten Methoden der Haartransplantation - individuell abgestimmt auf Ihre Bedürfnisse.'
              : 'We use the most advanced methods of hair transplantation - individually tailored to your needs.'}
          </p>
        </div>

        {/* Desktop Layout - 3 Cards side by side */}
        <div ref={containerRef} className="hidden lg:grid lg:grid-cols-3 gap-6">
          {techniques.map((technique, index) => (
            <div 
              key={technique.id} 
              data-technique-card
              className="h-full"
            >
              {/* Card - All cards equal on Desktop */}
              <div 
                className={`group relative rounded-2xl border transition-all duration-500 overflow-hidden h-full flex flex-col card-lift ${
                  isDark 
                    ? 'bg-white/[0.02] border-white/10 hover:border-[#7BA7C2]/30 hover:bg-white/[0.05]'
                    : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
                }`}
              >
                {/* Technique Image - Full width at top */}
                {technique.image && (
                  <div className="relative overflow-hidden">
                    <img 
                      src={technique.image} 
                      alt={`${technique.name} Haartransplantation Methode - Dion Hair Clinic`}
                      loading="lazy"
                      className="w-full h-48 object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    {/* Gradient overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0a0a0a]/60' : 'from-white/40'} to-transparent`} />
                    {/* Badge on image */}
                    <div className="absolute bottom-3 left-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                        isDark ? 'bg-black/40 text-[#7BA7C2]' : 'bg-white/80 text-[#5A8BA6]'
                      }`}>
                        {technique.idealFor}
                      </span>
                    </div>
                  </div>
                )}

                {/* Card Content */}
                <div className="p-5 flex flex-col flex-grow">
                  {/* Title */}
                  <h3 className={`text-xl font-bold mb-2 transition-colors duration-300 ${
                    isDark ? 'text-white group-hover:text-[#7BA7C2]' : 'text-gray-900 group-hover:text-[#5A8BA6]'
                  }`}>
                    {technique.name}
                  </h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{technique.subtitle}</p>

                  {/* Description */}
                  <p className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {technique.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {technique.stats.map((stat, statIndex) => {
                      const hasAsterisk = stat.value.includes('*');
                      const displayValue = hasAsterisk ? stat.value.replace('*', '') : stat.value;
                      
                      return (
                        <div 
                          key={statIndex}
                          className={`text-center p-3 rounded-xl border ${
                            isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <div className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {displayValue}
                            {hasAsterisk && <span className={`text-[10px] align-super ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>*</span>}
                          </div>
                          <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 gap-2 mb-5">
                    {technique.features.map((feature, featureIndex) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div 
                          key={featureIndex}
                          className={`flex items-center gap-3 p-3 rounded-xl border ${
                            isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <FeatureIcon className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Benefits List */}
                  <div className={`p-4 rounded-xl border mt-auto ${
                    isDark ? 'bg-[#7BA7C2]/5 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/5 border-[#5A8BA6]/20'
                  }`}>
                    <h4 className={`font-semibold mb-3 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'de' ? 'Vorteile:' : 'Benefits:'}
                    </h4>
                    <div className="space-y-2">
                      {technique.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center gap-2">
                          <Check className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Layout - Full cards like desktop, stacked */}
        <div ref={mobileContainerRef} className="lg:hidden space-y-6">
          {techniques.map((technique, index) => (
            <div 
              key={technique.id} 
              data-technique-card-mobile
              className="scroll-mt-20"
            >
              {/* Full Card */}
              <div 
                className={`relative rounded-2xl border transition-all duration-500 overflow-hidden ${
                  mobileActiveIndex === index 
                    ? isDark ? 'bg-white/[0.05] border-[#7BA7C2]/30' : 'bg-[#5A8BA6]/5 border-[#5A8BA6]/30'
                    : isDark ? 'bg-white/[0.02] border-white/10' : 'bg-white border-gray-200 shadow-sm'
                }`}
              >
                {/* Animated Glow Effect */}
                {mobileActiveIndex === index && isDark && (
                  <>
                    <div className="technique-card-glow" />
                    <div className="technique-card-glow-blur" />
                  </>
                )}
                
                {/* Technique Image - Full width at top */}
                {technique.image && (
                  <div className="relative overflow-hidden">
                    <img 
                      src={technique.image} 
                      alt={`${technique.name} Haartransplantation Methode - Dion Hair Clinic`}
                      loading="lazy"
                      className="w-full h-48 object-cover object-center"
                    />
                    {/* Gradient overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0a0a0a]/60' : 'from-white/40'} to-transparent`} />
                    {/* Badge on image */}
                    <div className="absolute bottom-3 left-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                        isDark ? 'bg-black/40 text-[#7BA7C2]' : 'bg-white/80 text-[#5A8BA6]'
                      }`}>
                        {technique.idealFor}
                      </span>
                    </div>
                  </div>
                )}

                {/* Card Content */}
                <div className="p-5">
                  {/* Title */}
                  <h3 className={`text-lg font-bold mb-2 transition-colors duration-300 ${
                    mobileActiveIndex === index 
                      ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                      : isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {technique.name}
                  </h3>
                  <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{technique.subtitle}</p>

                  {/* Description */}
                  <p className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    {technique.description}
                  </p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {technique.stats.map((stat, statIndex) => {
                      const hasAsterisk = stat.value.includes('*');
                      const displayValue = hasAsterisk ? stat.value.replace('*', '') : stat.value;
                      
                      return (
                        <div 
                          key={statIndex}
                          className={`text-center p-3 rounded-xl border ${
                            isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <div className={`text-lg font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {displayValue}
                            {hasAsterisk && <span className={`text-[10px] align-super ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>*</span>}
                          </div>
                          <div className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{stat.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 gap-2 mb-5">
                    {technique.features.map((feature, featureIndex) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div 
                          key={featureIndex}
                          className={`flex items-center gap-3 p-3 rounded-xl border ${
                            isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-100'
                          }`}
                        >
                          <FeatureIcon className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Benefits List */}
                  <div className={`p-4 rounded-xl border ${
                    isDark ? 'bg-[#7BA7C2]/5 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/5 border-[#5A8BA6]/20'
                  }`}>
                    <h4 className={`font-semibold mb-3 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {language === 'de' ? 'Vorteile:' : 'Benefits:'}
                    </h4>
                    <div className="space-y-2">
                      {technique.benefits.map((benefit, benefitIndex) => (
                        <div key={benefitIndex} className="flex items-center gap-2">
                          <Check className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 sm:mt-16 text-center">
          <p className={`mb-4 sm:mb-6 text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {language === 'de' 
              ? 'Nicht sicher, welche Technik für Sie am besten geeignet ist?'
              : 'Not sure which technique is best for you?'}
          </p>
          <Button
            onClick={onOpenConsultation}
            className="bg-gradient-to-r from-[#7BA7C2] to-[#5A8BA6] hover:from-[#5A8BA6] hover:to-[#7BA7C2] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg shadow-lg shadow-[#7BA7C2]/25"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {language === 'de' ? 'Kostenlose Beratung erhalten' : 'Get Free Consultation'}
          </Button>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 text-center">
          <p className={`text-xs max-w-2xl mx-auto ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            {language === 'de' 
              ? '* Die Anwachsrate variiert je nach Behandlungsregion und individueller Veranlagung. Im Tonsurbereich (Hinterkopf/Wirbel) kann die Anwachsrate aufgrund der geringeren Durchblutung niedriger ausfallen.'
              : '* Growth rate varies depending on treatment region and individual predisposition. In the tonsure area (back of head/crown), the growth rate may be lower due to reduced blood circulation.'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Techniques;
